import formatRate from './formatRate';

import React, { useEffect, useRef } from 'react';

import RoundedButton from 'components/RoundedButton/RoundedButton';

import formatSubscriptionBusinessDate from 'utility/formatSubscriptionBusinessDate';

import { moveLabels } from './moveTranslations';

import { purchaseMoney } from './PurchasedSeatsReview';

import styles from './BillingCapacityMove.module.scss';

/* eslint-disable max-len -- Keep localized financial notices together. */
export const setupLabels = {
  en: {
    title: 'Billing setup pending',
    completeTitle: 'Active-access billing enabled',
    subscription: 'Subscription ID',
    status: 'Setup status',
    incomplete: 'Setup incomplete',
    progress: 'Setup in progress',
    complete: '✓ Setup complete',
    afterSetup: 'After setup completes',
    effective: 'Enabled now',
    coverage: 'Seat coverage',
    purchased: 'Purchased seats',
    needed: 'Seats needed',
    shortfall: 'Seats missing',
    covered: '✓ Purchased capacity covers setup',
    updated: '✓ Purchased seats updated',
    before: 'Before update',
    after: 'After update',
    purchase: 'Seat purchases require a separate financial review and confirmation.',
    retry: 'Retry setup',
    requested: '✓ Retry requested',
    requesting: 'Requesting retry…',
    refresh: 'Refresh status',
    retryUnknown: 'The retry outcome could not be confirmed. Refresh status.',
    reference: 'Billing reference',
    dates: 'Existing billing dates',
  },
  ar: {
    title: 'إعداد الفوترة قيد الانتظار',
    completeTitle: 'تم تفعيل فوترة الوصول النشط',
    subscription: 'رقم الاشتراك',
    status: 'حالة الإعداد',
    incomplete: 'لم يكتمل الإعداد',
    progress: 'الإعداد قيد التنفيذ',
    complete: '✓ اكتمل الإعداد',
    afterSetup: 'بعد اكتمال الإعداد',
    effective: 'مفعّلة الآن',
    coverage: 'تغطية المقاعد',
    purchased: 'المقاعد المشتراة',
    needed: 'المقاعد المطلوبة',
    shortfall: 'المقاعد الناقصة',
    covered: '✓ السعة المشتراة تغطي الإعداد',
    updated: '✓ تم تحديث المقاعد المشتراة',
    before: 'قبل التحديث',
    after: 'بعد التحديث',
    purchase: 'شراء المقاعد يتطلب مراجعة مالية وتأكيدًا منفصلًا.',
    retry: 'إعادة محاولة الإعداد',
    requested: '✓ تم طلب إعادة المحاولة',
    requesting: 'جارٍ طلب إعادة المحاولة…',
    refresh: 'تحديث الحالة',
    retryUnknown: 'تعذر تأكيد نتيجة إعادة المحاولة. حدّث الحالة.',
    reference: 'مرجع الفوترة',
    dates: 'تواريخ الفوترة الحالية',
  },
};

export default function SetupRecoveryReview({
  preview,
  language,
  plan,
  receipt,
  retryPhase,
  onRetry,
  onRefresh,
  onEdit,
  onClose,
}) {
  const heading = useRef(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  const copy = setupLabels[language];
  const common = moveLabels[language];
  const complete = preview.status === 'configured';
  const action = preview.status === 'pending' ? preview.setupRecovery?.action : null;
  const value = (number) => <bdi dir='ltr'>{number ?? '—'}</bdi>;
  const { rates } = preview;
  const period = rates?.period;
  const knownCapacity = preview.required?.active != null && preview.purchased?.active != null;
  const missing = Math.max(0, (preview.required?.active || 0) - (preview.purchased?.active || 0));
  const calendar = formatSubscriptionBusinessDate(preview.currentTermEndsOn);
  const date = calendar
    ? new Intl.DateTimeFormat(language === 'ar' ? 'ar' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
        calendar: 'gregory',
        numberingSystem: language === 'ar' ? 'arab' : 'latn',
      }).format(new Date(`${preview.currentTermEndsOn}T00:00:00Z`))
    : '—';
  return (
    <div className={styles.review} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className={styles.context}>{common.context}</div>
      <h2 ref={heading} tabIndex={-1}>
        {complete ? copy.completeTitle : copy.title}
      </h2>
      <p>
        <strong>
          {preview.target.tenantName}
          {plan ? ` · ${plan}` : ''}
        </strong>
      </p>

      <div className={styles.timing}>
        <div>
          {copy.status}
          <strong className={complete ? styles.positive : styles.blocked}>
            {complete ? copy.complete : retryPhase === 'requested' ? copy.progress : copy.incomplete}
          </strong>
        </div>
        <div>
          {common.starts}
          <strong>{complete ? copy.effective : copy.afterSetup}</strong>
        </div>
      </div>
      {receipt && !complete && (
        <div className={styles.purchaseReceipt} role='status'>
          <strong>{copy.updated}</strong>
          <div className={styles.receiptValues}>
            <span>
              {copy.before} {value(receipt.before)}
            </span>
            <span>
              {copy.after} {value(receipt.after)}
            </span>
          </div>
        </div>
      )}
      {retryPhase && (
        <p role={retryPhase === 'unknown' ? 'alert' : 'status'}>
          <strong>
            {retryPhase === 'unknown' ? copy.retryUnknown : retryPhase === 'sending' ? copy.requesting : copy.requested}
          </strong>
        </p>
      )}
      <h3>{copy.coverage}</h3>
      <div className={styles.coverage}>
        <div>
          {copy.purchased}
          <strong>{value(preview.purchased?.active)}</strong>
        </div>
        <div>
          {copy.needed}
          <strong>{value(preview.required?.active)}</strong>
        </div>
      </div>
      <p className={missing ? styles.blocked : styles.positive}>
        <strong>
          {knownCapacity ? (
            missing ? (
              <>
                {copy.shortfall}: {value(missing)}
              </>
            ) : (
              copy.covered
            )
          ) : (
            '—'
          )}
        </strong>
      </p>
      <h3>{common.rates}</h3>
      <div className={styles.rates}>
        {[
          [common.active, 'active', common.existing],
          [common.inactive, 'inactive', common.launch],
        ].map(([label, key, source]) => (
          <div key={key}>
            {label}
            <strong>
              {value(formatRate(rates?.[key]) == null ? '—' : `${rates?.currency || ''} ${formatRate(rates?.[key])}`)}
            </strong>
            <small>
              {period ? `${period.count} ${common[period.unit] || period.unit}` : '—'} · {source}
            </small>
          </div>
        ))}
      </div>
      <div className={styles.setupDates}>
        <div>
          <small>{common.ends}</small>
          <strong>
            <bdi>{date}</bdi>
          </strong>
        </div>
        <div>
          <small>{copy.dates}</small>
          <strong className={styles.positive}>{common.unchanged}</strong>
        </div>
      </div>
      {action === 'edit_purchased_seats' && (
        <p>
          <small>{copy.purchase}</small>
        </p>
      )}

      <footer>
        <RoundedButton
          text={common.close}
          color={complete ? 'primary' : 'white'}
          onClick={onClose}
          isDisabled={retryPhase === 'sending'}
        />
        {!complete && (
          <RoundedButton
            text={copy.refresh}
            color={retryPhase ? 'primary' : 'white'}
            onClick={onRefresh}
            isDisabled={retryPhase === 'sending'}
          />
        )}
        {action === 'edit_purchased_seats' && onEdit && !retryPhase && (
          <RoundedButton text={common.edit} color='primary' onClick={() => onEdit(preview)} />
        )}
        {action === 'retry_setup' && preview.target.tenantId && !retryPhase && (
          <RoundedButton text={copy.retry} color='primary' onClick={onRetry} />
        )}
      </footer>
    </div>
  );
}
